import EmptyTableState from 'components/EmptyTableState';
import FilterListHeaderRedesign from 'components/Filters/FilterListHeaderRedesign';
import InvestigationEnvelopesListCount from 'components/Investigations/InvestigationEnvelopesListCount';
import LinkLookup from 'components/LinkLookup';
import ListFooterHooksBased from 'components/ListFooter/ListFooterHooksBased';
import LoadingIndicator from 'components/LoadingIndicator';
import Navbar from 'components/Navbar';
import daysAgo from 'components/utils/daysAgo';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import useInvestigation from 'hooks/investigations/useInvestigation';
import useInvestigationEnvelopes from 'hooks/investigations/useInvestigationEnvelopes';
import React from 'react';
import LoadingOverlay from 'react-loading-overlay-ts';
import { useParams } from 'react-router-dom';

const SOURCE_LABELS: Record<string, string> = {
  envelope: 'Envelope',
  thread: 'Thread',
  sender: 'Sender',
  search: 'Explore search',
};

const formatSource = (source?: string): string => (source ? SOURCE_LABELS[source] ?? source : '—');

const InvestigationDetailPage: React.FC = () => {
  const { investigationId } = useParams<{ investigationId: string }>();

  const {
    data: investigation,
    isLoading: investigationLoading,
    isFetching: investigationFetching,
  } = useInvestigation(investigationId, { redirectOnError: true });

  const {
    data: envelopesData,
    isLoading: envelopesLoading,
    isFetching: envelopesFetching,
  } = useInvestigationEnvelopes(investigationId);

  const isLoading = investigationLoading || envelopesLoading;
  const isFetching = investigationFetching || envelopesFetching;
  const envelopes = envelopesData?.records ?? [];

  if (!investigationId) {
    return null;
  }

  return (
    <div className="min-h-screen min-w-screen bg-white">
      <Navbar />
      <div className="pt-6">
        <header>
          <div className="max-w-7xl mx-auto px-6">
            <div className="mt-2 md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0 flex items-center">
                <h2 className="title">{investigation?.name ?? 'Investigation'}</h2>
                {isFetching && <LoadingIndicator className="ml-2" size="5" />}
              </div>
              <div className="mt-4 md:mt-0 flex-shrink-0 flex items-center gap-3">
                <LinkLookup
                  type="button"
                  className="button button--primary h-8"
                  routeName="agents-list"
                  routeParams={{ investigationId }}
                >
                  Agent Sessions
                </LinkLookup>
                <LinkLookup
                  routeName="envelope-list"
                  type="button"
                  className="button button--primary h-8"
                  queryParams={{
                    envelopes__investigation_uuid: investigationId,
                  }}
                >
                  Open in Explore
                </LinkLookup>
              </div>
            </div>

            {investigation && (
              <div className="mt-4 flex flex-col gap-2 text-body text-litlingo-gray-5">
                {investigation.description ? (
                  <p className="max-w-3xl">{investigation.description}</p>
                ) : null}
                <div className="flex flex-row flex-wrap gap-x-6 gap-y-1">
                  <span>
                    <span className="font-medium text-litlingo-gray-6">Discover ID: </span>
                    {investigation.discover_id ?? '—'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </header>
        <main>
          <div className="h-full mt-6 mb-8 max-w-7xl mx-auto px-6">
            <div className="flex justify-end items-center mb-2">
              <InvestigationEnvelopesListCount />
            </div>
            <LoadingOverlay
              active={isLoading}
              spinner={<LoadingIndicator size="10" />}
              fadeSpeed={0}
              styles={{
                content: (base) => ({
                  ...base,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }),
                wrapper: () => ({}),
                spinner: () => ({}),
                overlay: (base) => ({
                  ...base,
                  zIndex: 10,
                }),
              }}
            >
              <div className="table-wrapper__new h-full">
                <table className="table-wrapper__table h-full">
                  <thead>
                    <tr>
                      <th className="table-wrapper__new-th table-wrapper__th--w-30 pl-4">
                        Message
                      </th>
                      <th className="table-wrapper__new-th">Source</th>
                      <th className="table-wrapper__new-th">Created By</th>
                      <FilterListHeaderRedesign
                        resource={resourceQueryParamName.investigationEnvelopes}
                        text="Last Modified"
                        field="updated_at"
                      />
                    </tr>
                  </thead>
                  <tbody className="table-wrapper__tbody h-full">
                    {!isFetching && envelopes.length === 0 && (
                      <EmptyTableState
                        data-testid="empty-investigation-envelopes"
                        message="No messages linked yet."
                        colCount="4"
                      />
                    )}
                    {envelopes.map((row) => (
                      <tr
                        key={row.uuid}
                        className="table-wrapper__tbody-tr border-t border-gray-200 hover:bg-gray-100 table-row cursor-pointer"
                      >
                        <td className="table-wrapper__tbody-td pl-4 h-8">
                          <LinkLookup
                            routeName="envelope-detail"
                            routeParams={{ envelopeId: row.communication_envelope_uuid }}
                            className="text-litlingo-primary-100 underline text-body"
                          >
                            {row.communication_envelope_uuid}
                          </LinkLookup>
                        </td>
                        <td className="table-wrapper__tbody-td">
                          <span className="text-body text-litlingo-gray-5">
                            {formatSource(row.source)}
                          </span>
                        </td>
                        <td className="table-wrapper__tbody-td">
                          <span className="text-body text-litlingo-gray-5">
                            {row.created_by?.name ?? '—'}
                          </span>
                        </td>
                        <td className="table-wrapper__tbody-td">
                          <span className="text-body text-litlingo-gray-5">
                            {row.updated_at ? daysAgo(row.updated_at) : '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <ListFooterHooksBased
                  resourceStateName={resourceQueryParamName.investigationEnvelopes}
                  resourceName="message"
                  fetchHook={useInvestigationEnvelopes}
                />
              </div>
            </LoadingOverlay>
          </div>
        </main>
      </div>
    </div>
  );
};

export default InvestigationDetailPage;
